import { ENV } from "../../Utils/constants";
import { cabecerasApi } from "./cabeceras";

export class ReviEvaluationsApi {
  constructor() {
    this.baseApi = ENV.BASE_API;
  }

  async #safeJson(res) {
    try {
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  #throwHttp(res, json, fallback) {
    const msg =
      (json && (json.message || json.error)) ||
      fallback ||
      `Error HTTP ${res.status}`;
    throw new Error(msg);
  }

  async getCatalog(token, { page = 1, limit = 12, area, type, search } = {}) {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (area) params.append("area", area);
    if (type) params.append("type", type);
    if (search) params.append("search", search);

    const url = `${this.baseApi}/revi/evaluation?${params.toString()}`;
    const res = await fetch(url, {
      headers: cabecerasApi(token),
    });
    const json = await this.#safeJson(res);

    if (res.status !== 200)
      this.#throwHttp(res, json, "No se pudo cargar el catálogo");

    return {
      evaluations: (json && json.evaluations) || [],
      pagination: json && json.pagination,
    };
  }

  // GET /revi/evaluation/therapist/:id
  async getMyEvaluations(
    token,
    therapistId,
    { page = 1, limit = 12, area, search } = {},
  ) {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (area) params.append("area", area);
    if (search) params.append("search", search);

    const url = `${this.baseApi}/revi/evaluation/therapist/${therapistId}?${params.toString()}`;
    const res = await fetch(url, {
      headers: cabecerasApi(token),
    });
    const json = await this.#safeJson(res);

    if (res.status !== 200)
      this.#throwHttp(res, json, "No se pudieron cargar tus evaluaciones");

    return {
      evaluations: (json && json.evaluations) || [],
      pagination: json && json.pagination,
    };
  }

  // GET /revi/evaluation/:id (detalle completo con questions.text)
  async getEvaluationById(token, id, { signal } = {}) {
    const url = `${this.baseApi}/revi/evaluation/${id}`;
    const res = await fetch(url, {
      headers: cabecerasApi(token),
      signal,
    });
    const json = await this.#safeJson(res);

    if (res.status !== 200)
      this.#throwHttp(res, json, "No se pudo cargar el detalle");

    // IMPORTANT: devolvemos solo la evaluación
    return json.evaluation;
  }

  async createCustomEvaluation(token, payload) {
    const url = `${this.baseApi}/revi/evaluation`;
    const res = await fetch(url, {
      method: "POST",
      headers: { ...cabecerasApi(token), "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const json = await this.#safeJson(res);

    if (res.status !== 201)
      this.#throwHttp(res, json, "No se pudo crear la evaluación");

    return json.evaluation;
  }

  async updateCustomEvaluation(token, id, payload) {
    const url = `${this.baseApi}/revi/evaluation/${id}`;
    const res = await fetch(url, {
      method: "PATCH",
      headers: { ...cabecerasApi(token), "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const json = await this.#safeJson(res);

    if (res.status !== 200) this.#throwHttp(res, json, "No se pudo actualizar");

    return json.evaluation;
  }

  async deleteOrArchiveEvaluation(token, id) {
    const url = `${this.baseApi}/revi/evaluation/${id}`;
    const res = await fetch(url, {
      method: "DELETE",
      headers: cabecerasApi(token),
    });
    const json = await this.#safeJson(res);

    if (res.status !== 200) this.#throwHttp(res, json, "No se pudo eliminar");

    return true;
  }

  // GET /revi/evaluation/shared/all
  async getSharedEvaluations(token, { page = 1, limit = 12, search } = {}) {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (search) params.append("search", search);

    const url = `${this.baseApi}/revi/evaluation/shared/all?${params.toString()}`;
    const res = await fetch(url, {
      headers: cabecerasApi(token),
    });
    const json = await this.#safeJson(res);

    if (res.status !== 200)
      this.#throwHttp(res, json, "No se pudieron cargar compartidas");

    return {
      evaluations: (json && json.evaluations) || [],
      pagination: json && json.pagination,
    };
  }

  // POST /revi/evaluation/:id/submit
  // Guarda un resultado clínico para un paciente (con o sin sesión/terapia).
  async submitResult(
    token,
    id,
    { patientId, answers, comments = "", therapyId = null } = {},
  ) {
    const url = `${this.baseApi}/revi/evaluation/${id}/submit`;
    const res = await fetch(url, {
      method: "POST",
      headers: { ...cabecerasApi(token), "Content-Type": "application/json" },
      body: JSON.stringify({ patientId, answers, comments, therapyId }),
    });
    const json = await this.#safeJson(res);

    if (res.status !== 201)
      this.#throwHttp(res, json, "No se pudo guardar la evaluación");

    return json.result;
  }

  // POST /revi/evaluation/:id/clone
  async cloneEvaluation(token, id) {
    const url = `${this.baseApi}/revi/evaluation/${id}/clone`;
    const res = await fetch(url, {
      method: "POST",
      headers: cabecerasApi(token),
    });
    const json = await this.#safeJson(res);

    if (res.status !== 201) this.#throwHttp(res, json, "No se pudo clonar");

    return json.evaluation;
  }
}
